import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowUpRight, Clock3, Database, Eye, RefreshCw, Radio, Users, Wallet } from "lucide-react";
import CommandCenterWidgets, { SECTION_LINKS } from "./commandCenter/CommandCenterWidgets";
import AppLayout from "../components/AppLayout";
import { latestMemberDataTimestamp, latestMetricsByAccount, usePlatformData } from "../hooks/usePlatformData";
import { detectCurrency, formatCurrencyTotals, formatMoney, sumByCurrency } from "../lib/money";
import { api } from "../lib/api";

const REVENUE_PLATFORMS = ["onlyfans_vip", "onlyfans_free", "rawfuckclub", "theblowers"];
const AUDIENCE_PLATFORMS = [
  ["instagram", "Instagram", "Followers"],
  ["tiktok", "TikTok", "Followers"],
  ["onlyfans_free", "OnlyFans Free", "Followers"],
  ["theblowers", "TheBlowers", "Registrations"],
];

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
// Revenue is never added across currencies: one value per currency ("€1,234.00 · $567.00").
const statCurrency = (stat) => detectCurrency(stat?.currency);

function isHiddenPlatform(platform) {
  return ["justforfans", "jff"].includes(String(platform || "").toLowerCase().replace(/[^a-z0-9]/g, ""));
}

function validNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function dateValue(value) {
  const timestamp = value ? new Date(value) : null;
  return timestamp && Number.isFinite(timestamp.getTime()) ? timestamp : null;
}

function latestDate(values) {
  return values.map(dateValue).filter(Boolean).sort((left, right) => right - left)[0] || null;
}

function formatDate(value, options = { dateStyle: "medium", timeStyle: "short" }) {
  const timestamp = dateValue(value);
  return timestamp ? timestamp.toLocaleString(undefined, options) : "Not recorded";
}

function platformType(kind) {
  return ({ paid_ppv: "Paid creator", free_creator: "Free creator", studio: "Studio", affiliate: "Affiliate", social: "Social", wallet: "Wallet" })[kind] || "Platform";
}

function metricType(metric) {
  const label = `${metric.metric_key || ""} ${metric.normalized_key || ""} ${metric.original_label || ""}`.toLowerCase();
  if (/revenue|earning|income|sales|payout/.test(label)) return "revenue";
  if (/follower|subscriber|registration|audience|fan count/.test(label)) return "audience";
  return null;
}

function StatCard({ label, value, detail, icon: Icon, tone = "cyan" }) {
  const tones = {
    cyan: "text-cyan-300 border-cyan-400/20",
    green: "text-emerald-300 border-emerald-400/20",
    amber: "text-amber-300 border-amber-400/20",
    white: "text-zinc-100 border-zinc-700",
  };
  return <div className={`min-w-0 border-l-2 bg-zinc-950/70 px-4 py-4 ${tones[tone]}`}>
    <div className="flex items-center justify-between gap-2 text-xs font-medium text-zinc-400"><span>{label}</span><Icon size={15} className={tones[tone].split(" ")[0]} /></div>
    <p className="mt-2 break-words text-2xl font-semibold tabular-nums tracking-normal text-white sm:text-3xl">{value}</p>
    {detail && <p className="mt-1 text-xs text-zinc-500">{detail}</p>}
  </div>;
}

function Section({ title, eyebrow, icon: Icon, children, className = "" }) {
  return <section className={`min-w-0 rounded-md border border-zinc-800 bg-zinc-950/85 p-4 sm:p-5 ${className}`}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <div><p className="text-[10px] font-semibold uppercase text-cyan-300">{eyebrow}</p><h2 className="mt-1 flex items-center gap-2 text-base font-semibold text-white"><Icon size={16} className="text-cyan-300" />{title}</h2></div>
    </div>
    {children}
  </section>;
}

function DataSource({ label, status, updated, icon: Icon, tone = "cyan" }) {
  const color = tone === "green" ? "text-emerald-300" : tone === "amber" ? "text-amber-300" : "text-cyan-300";
  return <div className="min-w-0 border-t border-zinc-800 px-3 py-3 sm:border-l sm:border-t-0">
    <p className="flex items-center gap-2 text-xs font-medium text-zinc-300"><Icon size={14} className={color} />{label}</p>
    <p className={`mt-1 text-sm font-semibold ${color}`}>{status}</p>
    <p className="mt-1 text-[11px] text-zinc-500">{updated}</p>
  </div>;
}

// Phase 2: the five merged admin sections, reachable from the single admin home.
export { SECTION_LINKS };

export default function CreatorCommandCenter() {
  const data = usePlatformData();
  const [latestExtensionVersion, setLatestExtensionVersion] = useState(null);
  useEffect(() => {
    let active = true;
    Promise.resolve()
      .then(() => api.get("/api/extension/version"))
      .then((response) => { if (active) setLatestExtensionVersion(response.data?.version || null); })
      .catch(() => { if (active) setLatestExtensionVersion(null); });
    return () => { active = false; };
  }, []);
  const platforms = (data.platforms || []).filter((platform) => !isHiddenPlatform(platform.id) && platform.id !== "members");
  const savedStats = (data.platformStats || []).filter((stat) => !isHiddenPlatform(stat.platform));
  const membersData = data.membersData;
  const memberRecords = membersData?.records || [];
  const memberStats = membersData?.stats;
  const latestMemberUpdate = latestMemberDataTimestamp(memberRecords);
  const connectedAccounts = platforms.flatMap((platform) => (platform.accounts || []).map((account) => ({ ...account, platformId: platform.id, platformName: platform.name })));
  const installedExtensionVersions = Array.from(new Set(connectedAccounts.map((account) => String(account.extension_version || "").trim()).filter(Boolean)));
  const installedExtensionVersion = installedExtensionVersions.length ? installedExtensionVersions.join(", ") : "Not reported";
  const extensionUpdateAvailable = Boolean(latestExtensionVersion && installedExtensionVersions.some((version) => version !== latestExtensionVersion));
  const extensionMetrics = useMemo(() => latestMetricsByAccount((data.metrics || []).filter((metric) => !isHiddenPlatform(metric.platform))), [data.metrics]);

  const observed = useMemo(() => {
    const byPlatform = new Map();
    for (const metric of extensionMetrics) {
      const category = metricType(metric);
      const value = validNumber(metric.parsed_value);
      if (!category || value === null) continue;
      const platformId = String(metric.platform || "").toLowerCase();
      const accountId = metric.account_id || "default";
      const key = `${platformId}:${category}:${accountId}`;
      const current = byPlatform.get(key);
      if (!current || String(metric.captured_at || "") > String(current.updated || "")) {
        const { currency } = category === "revenue" ? detectCurrency(metric.currency, metric.raw_value, metric.unit) : { currency: null };
        byPlatform.set(key, { platformId, category, value, currency, updated: metric.captured_at || null });
      }
    }
    const totals = new Map();
    for (const entry of byPlatform.values()) {
      const key = `${entry.platformId}:${entry.category}`;
      const current = totals.get(key) || { value: 0, byCurrency: {}, updated: null };
      const byCurrency = { ...current.byCurrency };
      if (entry.currency) byCurrency[entry.currency] = (byCurrency[entry.currency] || 0) + entry.value;
      totals.set(key, { value: current.value + entry.value, byCurrency, updated: latestDate([current.updated, entry.updated])?.toISOString() || entry.updated });
    }
    return totals;
  }, [extensionMetrics]);

  const statsByPlatform = useMemo(() => new Map(savedStats.map((stat) => [String(stat.platform).toLowerCase(), stat])), [savedStats]);
  const savedRevenueRows = REVENUE_PLATFORMS.map((id) => statsByPlatform.get(id)).filter((stat) => validNumber(stat?.total_revenue) > 0);
  const revenueTotals = sumByCurrency(savedRevenueRows.map((stat) => ({
    amount: validNumber(stat.total_revenue),
    ...statCurrency(stat),
    label: stat.display_name || stat.platform,
  })));
  const totalSocialFollowers = AUDIENCE_PLATFORMS.slice(0, 3).reduce((sum, [id]) => {
    const saved = validNumber(statsByPlatform.get(id)?.followers);
    const extension = observed.get(`${id}:audience`)?.value;
    return sum + (extension ?? saved ?? 0);
  }, 0);
  const basicMembers = validNumber(memberStats?.by_tier?.basic);
  const vipMembers = validNumber(memberStats?.by_tier?.vip);
  const paidSubscribers = basicMembers !== null && vipMembers !== null ? basicMembers + vipMembers : null;
  const activeMembers = validNumber(memberStats?.by_status?.active);
  const totalMembers = validNumber(membersData?.total ?? memberStats?.total);
  const freeMembers = validNumber(memberStats?.by_tier?.free);

  const audienceRows = AUDIENCE_PLATFORMS.map(([id, name, label]) => {
    const saved = validNumber(statsByPlatform.get(id)?.followers);
    const extension = observed.get(`${id}:audience`);
    const value = extension?.value ?? saved;
    return value > 0 ? { id, name, label, value, updated: extension?.updated || statsByPlatform.get(id)?.last_updated } : null;
  }).filter(Boolean);

  const performanceRows = useMemo(() => {
    const names = new Map(platforms.map((platform) => [String(platform.id).toLowerCase(), platform.name]));
    const ids = new Set([...statsByPlatform.keys(), ...Array.from(observed.keys(), (key) => key.split(":")[0])]);
    return Array.from(ids).filter((id) => id !== "members" && !isHiddenPlatform(id)).map((id) => {
      const stat = statsByPlatform.get(id);
      const extensionRevenue = observed.get(`${id}:revenue`);
      const extensionAudience = observed.get(`${id}:audience`);
      const savedRevenue = validNumber(stat?.total_revenue) > 0 ? { [statCurrency(stat).currency]: validNumber(stat.total_revenue) } : null;
      const revenue = extensionRevenue ? extensionRevenue.byCurrency : savedRevenue;
      const revenueSort = revenue ? Math.max(0, ...Object.values(revenue)) : 0;
      const savedAudience = [validNumber(stat?.followers), validNumber(stat?.subscribers)].filter((value) => value > 0).reduce((sum, value) => sum + value, 0);
      const audience = extensionAudience?.value ?? (savedAudience > 0 ? savedAudience : null);
      if (!(revenueSort > 0) && !(audience > 0)) return null;
      const platform = platforms.find((item) => String(item.id).toLowerCase() === id);
      const lastUpdated = latestDate([extensionRevenue?.updated, extensionAudience?.updated, stat?.last_updated]);
      return {
        id,
        name: stat?.display_name || names.get(id) || platform?.name || id,
        type: platformType(stat?.kind),
        revenue,
        revenueSort,
        audience,
        status: extensionRevenue || extensionAudience ? "Extension data" : "Saved data",
        updated: lastUpdated,
      };
    }).filter(Boolean).sort((left, right) => (right.revenueSort || 0) - (left.revenueSort || 0) || (right.audience || 0) - (left.audience || 0));
  }, [platforms, statsByPlatform, observed]);

  const activeIds = new Set(performanceRows.map((row) => row.id));
  const setupPlatforms = platforms.filter((platform) => !activeIds.has(String(platform.id).toLowerCase()));
  const lastUpdate = latestDate([
    ...savedStats.map((stat) => stat.last_updated),
    latestMemberUpdate,
    ...connectedAccounts.map((account) => account.last_synced_at),
  ]);
  const savedStatsTimestamp = latestDate(savedStats.map((stat) => stat.last_updated));
  const latestExtensionSync = latestDate(connectedAccounts.map((account) => account.last_synced_at));
  const publishingRows = savedStats.map((stat) => ({
    ...stat,
    posts: validNumber(stat.posts_count),
    engagement: validNumber(stat.engagement_rate),
  })).filter((stat) => stat.posts > 0 || stat.engagement > 0)
    .sort((left, right) => (right.posts || 0) - (left.posts || 0));
  const recentSyncRuns = (data.syncRuns || [])
    .filter((run) => !isHiddenPlatform(run.platform))
    .slice()
    .sort((left, right) => new Date(right.completed_at || right.started_at || 0) - new Date(left.completed_at || left.started_at || 0))
    .slice(0, 5);
  const dataValue = (value, formatter = numberFormat.format) => value === null || value === undefined ? (data.loading ? "Loading" : "Unavailable") : formatter(value);

  return <AppLayout title="Command Center" overline="CREATOR OPERATIONS">
    <div className="mx-auto max-w-7xl space-y-4 pb-8">
      <header className="flex flex-col gap-4 border-b border-zinc-800 bg-[radial-gradient(ellipse_at_top_left,rgba(255,16,240,0.14),transparent_55%)] pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-xs font-semibold uppercase text-[#FF10F0]">Admin home</p><h1 className="mt-1 text-2xl font-semibold text-white">Today, revenue, members, content</h1><p className="mt-1 text-sm text-zinc-400">Live widgets from your own data. Each one loads on its own; nothing is estimated.</p></div>
        <div className="flex flex-wrap gap-2"><button type="button" onClick={data.reload} className="inline-flex min-h-10 items-center gap-2 rounded border border-zinc-700 bg-zinc-900 px-3 text-sm text-zinc-200 hover:border-cyan-500"><RefreshCw size={14} />Refresh</button><Link to="/crm/live-sync" className="inline-flex min-h-10 items-center gap-2 rounded border border-zinc-700 px-3 text-sm text-zinc-300 hover:border-cyan-500 hover:text-white">Live Sync <ArrowUpRight size={14} /></Link></div>
      </header>

      <CommandCenterWidgets />

      <section data-testid="extension-version-status" role={extensionUpdateAvailable ? "alert" : "status"} className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-l-2 px-3 py-2 text-sm ${extensionUpdateAvailable ? "border-amber-400 bg-amber-950/20 text-amber-200" : "border-zinc-700 bg-zinc-950/50 text-zinc-300"}`}>
        <span>Chrome extension</span>
        <span>Installed: {installedExtensionVersion}</span>
        <span>Latest: {latestExtensionVersion || "Unavailable"}</span>
        {extensionUpdateAvailable && <strong>Update available</strong>}
      </section>

      <h2 className="pt-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">Platforms &amp; audience details</h2>

      {data.error && <p role="alert" className="rounded border border-red-900 bg-red-950/30 px-4 py-3 text-sm text-red-300">{data.error}</p>}

      <section aria-label="Key performance indicators" className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Total platform revenue" value={savedRevenueRows.length ? formatCurrencyTotals(revenueTotals.totals) : dataValue(data.platformStats ? "No revenue recorded" : null, String)} detail={`All time · saved / manual · per currency, never added together${revenueTotals.assumed.length ? ` · USD assumed for ${revenueTotals.assumed.length} platform${revenueTotals.assumed.length === 1 ? "" : "s"} with no currency set` : ""}`} icon={Wallet} tone="green" />
        <StatCard label="Members Area total" value={dataValue(totalMembers)} detail="Internal member database" icon={Users} tone="cyan" />
        <StatCard label="Active members" value={dataValue(activeMembers)} detail="Current active status" icon={Activity} tone="green" />
        <StatCard label="Paid subscribers" value={dataValue(paidSubscribers)} detail="Basic + VIP tiers" icon={Wallet} tone="amber" />
        <StatCard label="Total social followers" value={dataValue(audienceRows.length ? totalSocialFollowers : null)} detail="Instagram · TikTok · OnlyFans Free" icon={Eye} tone="cyan" />
        <StatCard label="Last data update" value={lastUpdate ? formatDate(lastUpdate, { dateStyle: "medium" }) : (data.loading ? "Loading" : "Not recorded")} detail={lastUpdate ? formatDate(lastUpdate, { timeStyle: "short" }) : "Saved and internal sources"} icon={Clock3} tone="white" />
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Section title="All-time revenue by platform" eyebrow="Saved / manual revenue" icon={Wallet}>
          <p className="mb-3 text-xs text-zinc-500">Saved / manual revenue until extension sync is active for these platforms.</p>
          <div className="space-y-1">
            {savedRevenueRows.map((stat) => <div key={stat.platform} className="flex items-center justify-between gap-4 border-b border-zinc-800/80 py-3 last:border-0"><div className="min-w-0"><p className="truncate text-sm font-medium text-zinc-100">{stat.display_name || stat.platform}</p><p className="mt-1 text-xs text-zinc-500">Updated {formatDate(stat.last_updated)}</p></div><p className="shrink-0 text-lg font-semibold tabular-nums text-emerald-300">{formatMoney(validNumber(stat.total_revenue), statCurrency(stat).currency)}</p></div>)}
            {!savedRevenueRows.length && <p className="py-4 text-sm text-zinc-500">No saved revenue recorded.</p>}
          </div>
        </Section>

        <Section title="Audience" eyebrow="Followers and registrations" icon={Eye}>
          <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
            {audienceRows.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 border-b border-zinc-800/80 py-3"><div className="min-w-0"><p className="truncate text-sm font-medium text-zinc-100">{item.name}</p><p className="mt-1 text-xs text-zinc-500">{item.label}{item.updated ? ` · Updated ${formatDate(item.updated, { dateStyle: "medium" })}` : ""}</p></div><p className="text-lg font-semibold tabular-nums text-cyan-300">{numberFormat.format(item.value)}</p></div>)}
            {!audienceRows.length && <p className="py-4 text-sm text-zinc-500">No audience metrics recorded.</p>}
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Section title="Publishing & engagement" eyebrow="Recorded platform activity" icon={Activity}>
          <div className="divide-y divide-zinc-800">
            {publishingRows.map((stat) => <div key={stat.platform} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0"><p className="truncate text-sm font-medium text-zinc-100">{stat.display_name || stat.platform}</p><p className="mt-1 text-xs text-zinc-500">{stat.posts !== null ? `${numberFormat.format(stat.posts)} posts` : "Posts not reported"}</p></div>
              <div className="text-right"><p className="text-sm font-semibold tabular-nums text-cyan-300">{stat.engagement !== null ? `${stat.engagement.toLocaleString(undefined, { maximumFractionDigits: 1 })}%` : "Not reported"}</p><p className="text-xs text-zinc-500">engagement rate</p></div>
            </div>)}
            {!publishingRows.length && <p className="py-4 text-sm text-zinc-500">No publishing or engagement stats recorded.</p>}
          </div>
        </Section>

        <Section title="Recent sync activity" eyebrow="Latest platform updates" icon={Radio}>
          <div className="divide-y divide-zinc-800">
            {recentSyncRuns.map((run, index) => {
              const platformName = platforms.find((platform) => platform.id === run.platform)?.name || run.platform || "Platform";
              const runTime = run.completed_at || run.started_at;
              const status = String(run.status || "unknown");
              return <div key={run.sync_run_id || `${run.platform}-${run.started_at}-${index}`} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-zinc-100">{platformName}</p><p className="mt-1 text-xs text-zinc-500">{run.records_processed ?? 0} records · {runTime ? formatDate(runTime) : "Time unavailable"}</p></div>
                <span className={`shrink-0 text-xs font-medium capitalize ${status === "failed" ? "text-rose-300" : status === "completed" ? "text-emerald-300" : "text-amber-300"}`}>{status}</span>
              </div>;
            })}
            {!recentSyncRuns.length && <p className="py-4 text-sm text-zinc-500">No sync activity recorded.</p>}
          </div>
          <Link to="/crm/live-sync" className="mt-4 inline-flex items-center gap-1 border-t border-zinc-800 pt-3 text-xs font-medium text-cyan-300 hover:text-white">View live sync details <ArrowUpRight size={13} /></Link>
        </Section>
      </div>

      <Section title="Members Area" eyebrow="Internal data source · Connected" icon={Users}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          {[["Total members", totalMembers], ["Active members", activeMembers], ["Paid subscribers", paidSubscribers], ["Free members", freeMembers], ["VIP members", vipMembers], ["Basic members", basicMembers]].map(([label, value]) => <div key={label} className="border border-zinc-800 bg-zinc-900/60 px-3 py-3"><p className="text-xs text-zinc-500">{label}</p><p className="mt-1 text-xl font-semibold tabular-nums text-white">{dataValue(value)}</p></div>)}
        </div>
        <div className="mt-4 flex flex-col justify-between gap-3 border-t border-zinc-800 pt-3 sm:flex-row sm:items-center"><p className="text-xs text-zinc-400">Last member activity: <span className="text-zinc-200">{formatDate(latestMemberUpdate)}</span></p><Link to="/crm/members" className="inline-flex items-center gap-1 text-xs font-medium text-cyan-300 hover:text-white">Open Members page <ArrowUpRight size={13} /></Link></div>
        {memberRecords.length > 0 && <div className="mt-3 grid grid-cols-1 gap-x-5 sm:grid-cols-2 lg:grid-cols-3">{memberRecords.slice(0, 6).map((member, index) => <div key={member.member_id || member.id || index} className="flex min-w-0 items-center justify-between gap-3 border-b border-zinc-900 py-2"><div className="min-w-0"><p className="truncate text-sm text-zinc-200">{member.name || member.email || "Member"}</p><p className="text-xs capitalize text-zinc-500">{member.tier || "Tier unavailable"} · {member.status || "Status unavailable"}</p></div><time className="shrink-0 text-xs text-zinc-500">{formatDate(member.last_login || member.created_at, { dateStyle: "medium" })}</time></div>)}</div>}
      </Section>

      <Section title="Active platform performance" eyebrow="Platforms with recorded revenue or audience" icon={Activity}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-zinc-800 text-[11px] uppercase text-zinc-500"><tr><th className="py-2 pr-4 font-medium">Platform</th><th className="py-2 pr-4 font-medium">Type</th><th className="py-2 pr-4 text-right font-medium">Revenue</th><th className="py-2 pr-4 text-right font-medium">Audience</th><th className="py-2 pr-4 font-medium">Status</th><th className="py-2 font-medium">Last updated</th></tr></thead>
            <tbody>{performanceRows.map((row) => <tr key={row.id} className="border-b border-zinc-900 last:border-0"><td className="py-3 pr-4 font-medium text-zinc-100">{row.name}</td><td className="py-3 pr-4 text-zinc-400">{row.type}</td><td className="py-3 pr-4 text-right tabular-nums text-emerald-300">{row.revenueSort > 0 ? formatCurrencyTotals(row.revenue) : "Not reported"}</td><td className="py-3 pr-4 text-right tabular-nums text-cyan-300">{row.audience > 0 ? numberFormat.format(row.audience) : "Not reported"}</td><td className="py-3 pr-4 text-xs text-zinc-300">{row.status}</td><td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(row.updated, { dateStyle: "medium" })}</td></tr>)}</tbody>
          </table>
          {!performanceRows.length && <p className="py-4 text-sm text-zinc-500">No platform performance metrics recorded.</p>}
        </div>
        <details className="mt-4 border-t border-zinc-800 pt-3">
          <summary className="cursor-pointer text-xs font-medium text-zinc-400 hover:text-white">Connected / Setup · {setupPlatforms.length}</summary>
          {setupPlatforms.length > 0 && <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{setupPlatforms.map((platform) => <div key={platform.id} className="flex items-center justify-between gap-3 border border-zinc-800 px-3 py-2"><span className="truncate text-sm text-zinc-300">{platform.name}</span><span className="shrink-0 text-xs text-zinc-500">{platform.state === "connected" ? "Connected" : "Setup"}</span></div>)}</div>}
        </details>
      </Section>

      <section aria-label="Data source status" className="grid grid-cols-1 rounded-md border border-zinc-800 bg-zinc-950/80 sm:grid-cols-2 xl:grid-cols-4">
        <DataSource label="Saved stats" status={data.platformStats === null ? "Unavailable" : "Available"} updated={savedStatsTimestamp ? `Updated ${formatDate(savedStatsTimestamp)}` : "No update timestamp"} icon={Database} tone="green" />
        <DataSource label="Members Area" status={membersData ? "Internal · Connected" : "Unavailable"} updated={latestMemberUpdate ? `Updated ${formatDate(latestMemberUpdate)}` : "No activity timestamp"} icon={Users} tone="green" />
        <DataSource label="Extension sync" status={`${connectedAccounts.length} connected account${connectedAccounts.length === 1 ? "" : "s"}`} updated={latestExtensionSync ? `Last sync ${formatDate(latestExtensionSync)}` : "No sync timestamp"} icon={Radio} />
        <DataSource label="JustFor.Fans" status="Hidden" updated="Excluded from dashboard totals" icon={Eye} tone="amber" />
      </section>
    </div>
  </AppLayout>;
}